import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, select, Store} from '@ngrx/store';
import {L10nTranslationService} from 'angular-l10n';
import * as _ from 'lodash';
import {catchError, map, switchMap, withLatestFrom} from 'rxjs/operators';

import {debug} from '../../../rxjs-operators';
import {ImpersonableUsersActionTypes, LoadImpersonableUsersFailAction, LoadImpersonableUsersSuccessAction} from '../actions/impersonable-users';
import {error} from '../actions/toasts';
import {ArrayResponse} from '../models/array-response';
import {ImpersonableUserVM} from '../models/impersonable-user.vm';
import {SendBackResult} from '../models/send-back-result';
import {User} from '../models/user';
import * as fromShared from '../reducers';
import {ImpersonableUsersService} from '../services/impersonable-users.service';

import {BaseEffect} from './base.effect';

@Injectable()
export class ImpersonableUsersEffect extends BaseEffect {
  @Effect()
  load$ = this.actions$.pipe(
    ofType(ImpersonableUsersActionTypes.LOAD),
    debug('Load impersonable users action received.'),
    withLatestFrom(this.store.pipe(select(fromShared.selectors.getCurrentClient))),
    switchMap(([action, client]) => this.impersonableUsersService.getList(client.uuid)
                                      .pipe(map((result: SendBackResult<ArrayResponse<User>>) => _.map(result.data.array, mapUserToImpersonableUserVM)),
                                            map((payload: ImpersonableUserVM[]) => new LoadImpersonableUsersSuccessAction(payload)),
                                            catchError((res: Response) => this.catchResponseError(res)))));

  @Effect()
  reload$ = this.actions$.pipe(
    ofType(ImpersonableUsersActionTypes.RELOAD),
    debug('Reload impersonable users action received.'),
    withLatestFrom(this.store.pipe(select(fromShared.selectors.getCurrentClient))),
    switchMap(([action, client]) => this.impersonableUsersService.getList(client.uuid)
                                      .pipe(map((result: SendBackResult<ArrayResponse<User>>) => _.map(result.data.array, mapUserToImpersonableUserVM)),
                                            map((payload: ImpersonableUserVM[]) => new LoadImpersonableUsersSuccessAction(payload)),
                                            catchError((res: Response) => this.catchResponseError(res)))));

  @Effect()
  loadFail$ = this.actions$.pipe(ofType(ImpersonableUsersActionTypes.LOAD_FAIL),
                                 debug('A server error occurred while retrieving impersonable users.'),
                                 withLatestFrom(this.store.select(fromShared.selectors.getCurrentClient)),
                                 map(([action, client]) => error(this.translation.translate('USER_CONTEXT_GET_IMPERSONABLE_ERROR', { clientName: client.name }),
                                                                 this.translation.translate('TOAST_ERROR_TITLE'))));

  constructor(private actions$: Actions,
              private store: Store<fromShared.AppState>,
              private translation: L10nTranslationService,
              private impersonableUsersService: ImpersonableUsersService,
              router: Router) {
    super(router);
  }

  protected handleUnhandledError(response: Response): Action { return new LoadImpersonableUsersFailAction(response.status); }
}

function mapUserToImpersonableUserVM(user: User): ImpersonableUserVM {
  return { uuid: user.uuid, name: user.name, email: user.email, groupRoleName: _.get(user, 'groupRole.name', 'N/A') };
}
